//import config from "../../config.js";
import config from "@libs/config.js";
import backEndWsc from "../api/backEndWsc.js";
import unitConversionService from "@libs/unitConversionService.js";

// 끊긴 동안 폴백 재연결을 시도하는 간격(초). 트리거 정책은 모두 이 어댑터가 관장한다.
const RECONNECT_INTERVAL_SEC = 30;

// 서버가 분 단위 하트비트(UPDATE_LIVE_REAL)·KEEP_ALIVE 를 보내므로, 이 시간 동안 어떤 메시지도
// 받지 못하면 readyState 가 OPEN 이어도 좀비 소켓으로 보고 강제 재연결한다. (iOS Safari 방어)
const HEARTBEAT_TIMEOUT_SEC = 90;
// 좀비 여부를 점검하는 주기(초).
const HEARTBEAT_CHECK_INTERVAL_SEC = 15;
// close 직후 즉시 재연결의 최소 간격(초). 서버가 죽어 connect→onclose 가 연쇄될 때
// 백오프 없는 tight loop 가 도는 것을 막고, 이 간격 안의 재시도는 30초 폴백 타이머에 맡긴다.
const IMMEDIATE_RECONNECT_DEBOUNCE_SEC = 5;

const wsc = {
    client: null,
    wssUrl: config.backend.wssUrl,
    onVisibilityChange: null,
    onPageShow: null,
    reconnectTimer: null,
    heartbeatTimer: null,
    lastMessageAt: 0,
    lastReconnectAt: 0,
    createWsc(store) {
        let that = this;
        try {
            if (store == null) throw "backEndWsc store가 제공되지 않았습니다.";

            if (that.client != null) {
                // mounted 가 여러번 호출되면, wsc 인스턴스가 여러개 생성될 수 있음.
                // console.debug("WSC 이미 존재합니다.");
                return;
            }

            // console.debug("WSC를 생성합니다.");

            that.client = new backEndWsc(config.backend.wssUrl, {
                open: () => {
                    store.commit("UPDATE_IS_WSC_CONNECTED", true);

                    // 연결이 복구됐으니 폴백 재연결 타이머를 멈춘다.
                    that.stopReconnectTimer();

                    // 무수신 감시 타이머를 (재)기동한다. 갓 열린 연결을 즉시 timeout 처리하지 않도록
                    // 마지막 수신 시각을 현재로 리셋한다.
                    that.lastMessageAt = Date.now();
                    that.startHeartbeatMonitor();

                    // (재)연결 시 totalReal 전체 스냅샷을 받아 보정한다.
                    // 서버는 연결 후 변경분(델타)만 보내고 전체 스냅샷은 보내지 않으므로,
                    // 끊긴 동안 놓친 변경·미보고 관제점을 이 시점에 한 번 따라잡는다.
                    store.dispatch("FETCH_TOTAL_REAL_SEED");
                },
                receive: function ({ data }) {
                    try {
                        // console.log(data);

                        // 메시지가 들어올 때마다 생존 신호로 기록한다. 하트비트 워치독이 이 값을 본다.
                        that.lastMessageAt = Date.now();

                        if (that.onreceive) that.onreceive(store, JSON.parse(data));
                    } catch (err) {
                        console.error("WSC receive", err);
                    }
                },
                close: function () {
                    store.commit("UPDATE_IS_WSC_CONNECTED", false);

                    // destroyWsc 로 의도적으로 닫은 경우(that.client === null)는 재연결하지 않는다.
                    if (that.client) {
                        // 끊김을 확인한 바로 이 순간(아이콘 표시 시점) 즉시 한 번 재연결을 시도한다.
                        // iOS Safari 처럼 onclose 가 visibilitychange 보다 늦게 떠 fast-path 를 놓친 경우,
                        // 30초 폴백 타이머를 기다리지 않고 곧바로 따라잡기 위함이다.
                        // 단, 직전 시도와 너무 가까우면(서버 다운 등으로 connect→onclose 연쇄) 건너뛰어
                        // 백오프 없는 tight loop 를 막고 폴백 타이머에 맡긴다.
                        if (Date.now() - that.lastReconnectAt > IMMEDIATE_RECONNECT_DEBOUNCE_SEC * 1000) {
                            that.lastReconnectAt = Date.now();
                            that.reconnect();
                        }
                        // 즉시 시도가 실패하거나 디바운스로 건너뛴 경우를 대비한 폴백 타이머.
                        that.startReconnectTimer();
                    }
                },
            });

            // console.debug("WSC 시작합니다.");
            that.client.start();

            that.registerForegroundHandlers();
        } catch (err) {
            console.error("WSC", err);
        }
        return this;
    },
    /**
     * 끊긴 상태면 재연결을 시도한다. 모든 재연결 트리거(폴백 타이머·visibilitychange)가
     * 거쳐 가는 단일 진입점이다. start() 는 내부 소켓이 살아있으면 no-op 이라,
     * 트리거가 동시에 불려도 중복 연결되지 않는다.
     * @returns {void}
     */
    reconnect() {
        let that = this;
        if (that.client && !that.client.isConnected) {
            that.client.start();
        }
    },
    /**
     * 끊긴 동안 RECONNECT_INTERVAL_SEC 간격으로 재연결을 시도하는 폴백 타이머를 켠다.
     * 포그라운드에서 연결만 끊긴 경우(탭 전환이 없어 visibilitychange 가 안 뜨는 상황)를 커버한다.
     * 연결이 복구되면 open 콜백의 stopReconnectTimer 로 멈춘다.
     * @returns {void}
     */
    startReconnectTimer() {
        let that = this;
        if (that.reconnectTimer) return; // 이미 동작 중이면 중복 생성 방지

        that.reconnectTimer = setInterval(() => {
            console.log(`WebSocket try reconnect... ${RECONNECT_INTERVAL_SEC}`);
            that.reconnect();
        }, RECONNECT_INTERVAL_SEC * 1000);
    },
    /**
     * 폴백 재연결 타이머를 멈춘다.
     * @returns {void}
     */
    stopReconnectTimer() {
        let that = this;
        if (that.reconnectTimer) {
            clearInterval(that.reconnectTimer);
            that.reconnectTimer = null;
        }
    },
    /**
     * 메시지 무수신 감시 타이머를 켠다. HEARTBEAT_TIMEOUT_SEC 동안 한 건도 못 받으면
     * readyState 가 OPEN 이어도 좀비로 보고 강제 재연결한다.
     * onclose 가 끝내 발화하지 않는 iOS Safari 좀비 소켓을 잡기 위한 방어책이다.
     * @returns {void}
     */
    startHeartbeatMonitor() {
        let that = this;
        if (that.heartbeatTimer) return; // 이미 동작 중이면 중복 생성 방지

        that.heartbeatTimer = setInterval(() => {
            if (!that.client) return;

            const silentMs = Date.now() - that.lastMessageAt;
            if (silentMs > HEARTBEAT_TIMEOUT_SEC * 1000) {
                console.log(`WebSocket heartbeat timeout (${Math.round(silentMs / 1000)}s) → forceReconnect`);
                // 재연결 시도 직후 다음 점검에서 곧바로 또 트리거되지 않도록 시각을 당겨둔다.
                that.lastMessageAt = Date.now();
                that.client.forceReconnect();
            }
        }, HEARTBEAT_CHECK_INTERVAL_SEC * 1000);
    },
    /**
     * 무수신 감시 타이머를 멈춘다.
     * @returns {void}
     */
    stopHeartbeatMonitor() {
        let that = this;
        if (that.heartbeatTimer) {
            clearInterval(that.heartbeatTimer);
            that.heartbeatTimer = null;
        }
    },
    /**
     * 소켓이 실제로 살아있지 않으면(isAlive false) 즉시 강제 재연결한다.
     * 포그라운드 복귀 트리거(visibilitychange·pageshow)의 공통 동작이다.
     * 추정 플래그(isConnected)가 아니라 실제 readyState 로 판단해, iOS Safari 에서
     * onclose 가 늦게 떠 플래그가 stale 인 상황에도 간격을 기다리지 않고 따라잡는다.
     * @returns {void}
     */
    reconnectIfDead() {
        let that = this;
        if (that.client && !that.client.isAlive()) {
            that.client.forceReconnect();
        }
    },
    /**
     * 포그라운드 복귀 시 즉시 재연결을 트리거하는 핸들러들을 등록한다.
     * - visibilitychange: 탭/앱이 다시 보일 때.
     * - pageshow(persisted): iOS Safari 가 bfcache 로 얼려둔 페이지를 복원할 때.
     *   이 경우 visibilitychange 가 안 뜰 수 있어 별도로 받는다. 일반 로드(persisted=false)는
     *   신규 연결이 이미 처리하므로 무시한다.
     * @returns {void}
     */
    registerForegroundHandlers() {
        let that = this;
        if (that.onVisibilityChange) return; // 중복 등록 방지

        that.onVisibilityChange = () => {
            if (document.visibilityState !== "visible") return;
            that.reconnectIfDead();
        };
        document.addEventListener("visibilitychange", that.onVisibilityChange);

        that.onPageShow = (event) => {
            if (!event.persisted) return; // bfcache 복원만 처리
            that.reconnectIfDead();
        };
        window.addEventListener("pageshow", that.onPageShow);
    },
    destroyWsc() {
        let that = this;
        try {
            that.stopReconnectTimer();
            that.stopHeartbeatMonitor();

            if (that.onVisibilityChange) {
                document.removeEventListener("visibilitychange", that.onVisibilityChange);
                that.onVisibilityChange = null;
            }
            if (that.onPageShow) {
                window.removeEventListener("pageshow", that.onPageShow);
                that.onPageShow = null;
            }
            if (that.client != null) {
                console.debug("WSC를 제거합니다.");
                that.client.destroy();
                that.client = null;
            }
        } catch (err) {
            console.error("WSC", err);
        }
    },
    // onreceive:null,
    onreceive(store, msg) {
        try {
            // console.log(msg);
            // console.debug("WSC onreceive ", msg.command );
            // console.debug("WSC onreceive ", msg.command, JSON.stringify(msg) );

            // mutations 실행
            //store.commit(msg.command, msg);

            // action 실행
            // store.dispatch("UPDATE_MENUS");

            if (msg.command === "UPDATE_PT_LIST") store.commit(msg.command, msg);
            else if (msg.command === "UPDATE_TOTAL_REAL") store.commit(msg.command, msg);
            // 서버 커맨드명은 UPDATE_LIVE_REAL 그대로지만, 실시간 값은 totalReal 로 통합되어
            // 이 메시지는 서버시계 동기화 + 분 단위 하트비트 용도로만 쓴다.
            else if (msg.command === "UPDATE_LIVE_REAL") store.commit("SYNC_SERVER_CLOCK", msg);
            else if (msg.command === "UPDATE_ALARM_LOGS") store.commit(msg.command, msg);
            else if (msg.command === "UPDATE_ALARM_LOGS2") store.commit(msg.command, msg);
            else if (msg.command === "UPDATE_EQUIP_CTRL") store.commit(msg.command, msg);
            else if (msg.command === "UPDATE_FACILITY_OP_INFO") store.commit(msg.command, msg);
            else if (msg.command === "UPDATE_KEEP_ALIVE") store.commit(msg.command, msg);
            else if (msg.command === "UPDATE_UNIT_CONVERSION") {
                // 환산계수 업데이트 처리
                // msg.conversionData = { version, conversion }
                if (msg.conversionData && msg.conversionData.version) {
                    unitConversionService.handleWebSocketUpdate(msg.conversionData);
                }
            }
        } catch (err) {
            console.error("WSC", err);
        }
    },
};

export default wsc;
